import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlanHistoryFilter from "./filter";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../../../redux/loader/actions";
import pricingApi from "../../../../services/product_pricing";
import CustomDatagrid from "../../../../components/datagrid";
import moment from "moment";

const PlanHistoryModal = ({
  open,
  onClose,
  setPriceData,
  priceData,
  setManageMode,
  manageMode,
  setPlanDetails,
  setInitialPricingData,
  initialPricingData,
  setPlanName,
  planName,
  setProductList,
  setSample,
  sample,
  showProductPrice,
  setShowProductPrice,
  setPaginationModel,
  paginationModel,
  resetFields,
}) => {
  const dispatch = useDispatch();
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems
    ?.find((item) => item.name.toLowerCase() === "products")
    ?.submenus?.find((item) => item.name.toLowerCase() === "pricing");
  const isDisabled = createMenuItem?.access_type === "read_write";
  const [filter, setFilter] = useState({
    fromDate: null,
    toDate: null,
    status: "",
  });
  const [pricingData, setPricingData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [sortOrder, setSortOrder] = useState([{ field: "id", sort: "desc" }]);
  const [searchparams, setSearchparams] = useState(null);
  const [noData, setNoData] = useState(true);

  const handleFilter = (newFilter) => {
    setFilter(newFilter);
  };

  const fetchPricing = useCallback(async () => {
    if (!searchparams) {
      setPricingData([]);
      setRowCount(0);
      return;
    }
    dispatch(showLoader());
    try {
      const response = await pricingApi.getAllPricing(
        paginationModel.page + 1,
        paginationModel.pageSize,
        searchparams
      );

      const responseData = response?.data?.data || [];
      if (responseData) {
        setPricingData(responseData);
        setRowCount(responseData.pagination.totalItems);
        // setSample("edit");
        console.log("Sample set to:", "edit");
      } else {
        console.warn("Unexpected response format:", responseData);
        setPricingData([]);
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
    } finally {
      dispatch(hideLoader());
    }
  }, [paginationModel.page, paginationModel.pageSize, searchparams, dispatch]);

  useEffect(() => {
    if (searchparams) {
      fetchPricing();
    } else {
      setPricingData([]);
      setRowCount(0);
    }
  }, [fetchPricing, paginationModel, searchparams]);

  const sampleEditMode = useCallback(() => {
    setManageMode("edit");
  }, [setManageMode]);

  const sampleViewMode = () => {
    setManageMode("view");
  };

  const getProductPricingByIdEdit = (id) => {
    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setPriceData(res.data.data);
        setPlanDetails([]);
        setPlanName([]);
        // sampleEditMode();
        setManageMode("edit");
        onClose();
        setShowProductPrice(true);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      console.error(err);
    };
    pricingApi.getProductPricing(id).then(onSuccess, onFailure);
  };

  const getProductPricingById = useCallback(
    (id) => {
      dispatch(showLoader());
      pricingApi
        .getProductPricing(id)
        .then((res) => {
          dispatch(hideLoader());
          if (res?.data?.status === true) {
            // setProductList([]);
            // setManageMode("view");
          }
          onClose();
          setPriceData(res.data.data);
          setPlanDetails([]);
          // setInitialPricingData([]);
          setPlanName([]);
          sampleViewMode();
          setShowProductPrice(true);
        })
        .catch((err) => {
          dispatch(hideLoader());
          console.error(err);
        });
    },
    [dispatch]
  );

  const updatePricingStatus = useCallback(
    async (productId, newStatus) => {
      dispatch(showLoader());
      const data = { package_status: newStatus };
      try {
        const response = await pricingApi.updateStatus(productId, data);
        if (response?.data?.status) {
          fetchPricing();
        }
      } catch (error) {
        console.error("Error updating pricing:", error);
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, fetchPricing]
  );

  const isOngoing1Exist = pricingData?.data?.find(
    (data) => data?.computed_package_status === "ongoing1"
  );

  const priceListRows = Array.isArray(pricingData.data)
    ? pricingData.data.map((data, index) => {
        const packageStatus = data?.computed_package_status;
        let finalStatus = packageStatus;
        if (data?.computed_package_status === "ongoing1") {
          finalStatus = "ongoing";
        } else if (
          data?.computed_package_status === "ongoing2" &&
          !isOngoing1Exist
        ) {
          finalStatus = "ongoing";
        } else if (
          data?.computed_package_status === "ongoing2" &&
          isOngoing1Exist
        ) {
          finalStatus = "completed";
        }

        return {
          sno_id: index + 1,
          id: data?.prd_plan_package_uuid || index,
          created_at: data.created_at,
          offer_valid_from:
            data?.offer_valid_from === null
              ? "-"
              : moment
                  .utc(data?.offer_valid_from)
                  .startOf("day")
                  .format("DD/MM/YYYY"),
          offer_valid_to:
            data?.offer_valid_to === null
              ? "-"
              : moment
                  .utc(data?.offer_valid_to)
                  .endOf("day")
                  .format("DD/MM/YYYY"),
          offer_text: data?.offer_text === "" ? "-" : data?.offer_text,
          package_status: finalStatus,
          product: data?.prd_plan_package_uuid,
        };
      })
    : [];

  const columns = [
    {
      field: "sno_id",
      headerName: "S.No",
      width: 100,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "created_at",
      headerName: "Pricing Fix Date",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        moment(params.row.created_at).format("DD/MM/YYYY"),
    },
    {
      field: "offer_valid_from",
      headerName: "Offer Valid From",
      width: 250,
      headerAlign: "left",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      disableColumnMenu: true,
    },
    {
      field: "offer_valid_to",
      headerName: "Offer Valid To",
      width: 200,
      headerAlign: "left",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
      disableColumnMenu: true,
    },
    {
      field: "offer_text",
      headerName: "Offer Description",
      width: 200,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return <span>{params.value}</span>;
      },
    },
    {
      field: "package_status",
      headerName: "Status",
      width: 150,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div style={{ textAlign: "left", width: "100%" }}>
          <Chip
            label={
              params.row.package_status.charAt(0).toUpperCase() +
              params.row.package_status.slice(1)
            }
            sx={{ cursor: "pointer" }}
            color="primary"
            variant="outlined"
          />
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 200,
      headerAlign: "left",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { package_status } = params.row;
        return (
          <div style={{ textAlign: "left", width: "100%" }}>
            {package_status === "upcoming" && (
              <>
                {isDisabled && (
                  <Chip
                    label="Edit"
                    clickable
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ marginRight: "5px" }}
                    onClick={() => {
                      getProductPricingByIdEdit(params.row.product);
                    }}
                  />
                )}
                <Chip
                  label="View"
                  clickable
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ marginRight: "5px" }}
                  onClick={() => {
                    getProductPricingById(params.row.product);
                  }}
                />
                {isDisabled && (
                  <Chip
                    label="Cancel"
                    clickable
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      updatePricingStatus(params.row.product, "cancel")
                    }
                  />
                )}
              </>
            )}
            {package_status === "completed" && (
              <Chip
                label="View"
                clickable
                color="primary"
                variant="outlined"
                size="small"
                onClick={() => {
                  getProductPricingById(params.row.product);
                }}
              />
            )}
            {package_status === "ongoing" && (
              <>
                <Chip
                  label="View"
                  clickable
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ marginRight: "5px" }}
                  onClick={() => {
                    getProductPricingById(params.row.product);
                  }}
                />
                {/* {isDisabled && (
                  <Chip
                    label="Stop"
                    clickable
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      updatePricingStatus(params.row.product, "stop")
                    }
                  />
                )} */}
              </>
            )}
            {(package_status === "stopped" ||
              package_status === "cancelled") && (
              <Chip
                label="View"
                clickable
                color="primary"
                variant="outlined"
                size="small"
                onClick={() => {
                  getProductPricingById(params.row.product);
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="history-modal-title"
      aria-describedby="history-modal-description"
      disableScrollLock
    >
      <Box
        sx={{
          width: { xs: "76%", sm: "90%" },
          maxHeight: "80%",
          overflow: "auto",
          padding: 4,
          margin: "auto",
          marginTop: { xs: "8%", sm: "3%" },
          bgcolor: "white",
          borderRadius: "8px",
          position: "relative",
        }}
      >
        <IconButton
          onClick={() => {
            onClose();
            // setPriceData([]);
            // setSearchparams();
          }}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6">History Details</Typography>

        <PlanHistoryFilter
          onFilter={handleFilter}
          open={open}
          setSearchparams={setSearchparams}
          searchparams={searchparams}
          fetchPricing={fetchPricing}
          showProductPrice={showProductPrice}
          setShowProductPrice={setShowProductPrice}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          setPlanDetails={setPlanDetails}
          setManageMode={setManageMode}
          setPriceData={setPriceData}
          setInitialPricingData={setInitialPricingData}
          setPlanName={setPlanName}
          resetFields={resetFields}
          setNoData={setNoData}
        />

        <TableContainer component={Paper}>
          {noData ? (
            <Typography variant="h6" sx={{ textAlign: "center", padding: 2 }}>
              Please select a Product
            </Typography>
          ) : priceListRows.length === 0 ? (
            <Typography variant="h6" sx={{ textAlign: "center", padding: 2 }}>
              No Data Found
            </Typography>
          ) : (
            <CustomDatagrid
              rows={priceListRows}
              columns={columns}
              getRowId={(row) => row.id}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              rowCount={rowCount}
              onSortModelChange={setSortOrder}
              disableSelectionOnClick={true}
              checkboxSelection={false}
              pagination
              pageSizeOptions={[5, 10, 20]}
            />
          )}
        </TableContainer>
      </Box>
    </Modal>
  );
};

export default PlanHistoryModal;
